/**
 * Subscription status and feature flags — mirrors iOS SubscriptionService.
 *
 * During the 30-day trial (when enforcement is on), all features are enabled.
 * After trial expires, features follow the actual subscription tier.
 * When enforcement is off, all features stay enabled for everyone (current iOS default).
 */

import {
  parseSubscriptionTier,
  tierDisplayName,
  tierIncludesAIPlan,
  tierIncludesLevel1,
  tierIncludesLevel2,
  tierIncludesPlacesImages,
  type SubscriptionTier,
} from './subscriptionTiers';
import {
  getOrSetTrialStartDate,
  isTrialActive as trialIsActive,
  readTrialStartDate,
  trialDaysRemaining as remainingTrialDays,
} from './trialService';

const TIER_CACHE_KEY = 'wt_subscription_tier';

/** Match iOS: false keeps all features on for everyone until paywall ships. */
function envEnforceSubscriptions(): boolean {
  return import.meta.env.VITE_ENFORCE_SUBSCRIPTIONS === 'true';
}
let enforceSubscriptions = envEnforceSubscriptions();

let actualTier: SubscriptionTier = loadCachedTier();

function loadCachedTier(): SubscriptionTier {
  try {
    const raw = localStorage.getItem(TIER_CACHE_KEY);
    return parseSubscriptionTier(raw) ?? 'free';
  } catch {
    return 'free';
  }
}

function cacheTier(tier: SubscriptionTier): void {
  try {
    localStorage.setItem(TIER_CACHE_KEY, tier);
  } catch {
    /* ignore */
  }
}

function effectiveFlag(tierAllows: boolean): boolean {
  if (!enforceSubscriptions) return true;
  if (trialIsActive()) return true;
  return tierAllows;
}

export type SubscriptionFeature = 'aiPlan' | 'placesImages' | 'level1' | 'level2';

export interface SubscriptionSnapshot {
  enforceSubscriptions: boolean;
  actualTier: SubscriptionTier;
  actualTierDisplayName: string;
  isTrialActive: boolean;
  trialDaysRemaining: number;
  trialStartDate: string | null;
  includesPlacesImages: boolean;
  includesAIPlan: boolean;
  includesLevel1: boolean;
  includesLevel2: boolean;
}

export function getEnforceSubscriptions(): boolean {
  return enforceSubscriptions;
}

/** Test/admin override; production uses VITE_ENFORCE_SUBSCRIPTIONS. */
export function setEnforceSubscriptions(enabled: boolean): void {
  enforceSubscriptions = enabled;
}

export function getActualTier(): SubscriptionTier {
  return actualTier;
}

export function updateTier(tier: SubscriptionTier): void {
  actualTier = tier;
  cacheTier(tier);
}

export function updateTierFromString(tierString: string): void {
  const parsed = parseSubscriptionTier(tierString);
  if (parsed) {
    updateTier(parsed);
  } else {
    console.warn(`SubscriptionService: Unknown tier '${tierString}', defaulting to free`);
    updateTier('free');
  }
}

export function includesPlacesImages(): boolean {
  return effectiveFlag(tierIncludesPlacesImages(actualTier));
}

export function includesAIPlan(): boolean {
  return effectiveFlag(tierIncludesAIPlan(actualTier));
}

export function includesLevel1(): boolean {
  return effectiveFlag(tierIncludesLevel1(actualTier));
}

export function includesLevel2(): boolean {
  return effectiveFlag(tierIncludesLevel2(actualTier));
}

export function includesFeature(feature: SubscriptionFeature): boolean {
  switch (feature) {
    case 'aiPlan':
      return includesAIPlan();
    case 'placesImages':
      return includesPlacesImages();
    case 'level1':
      return includesLevel1();
    case 'level2':
      return includesLevel2();
  }
}

export function getSubscriptionSnapshot(): SubscriptionSnapshot {
  const start = readTrialStartDate();
  return {
    enforceSubscriptions,
    actualTier,
    actualTierDisplayName: tierDisplayName(actualTier),
    isTrialActive: trialIsActive(),
    trialDaysRemaining: remainingTrialDays(),
    trialStartDate: start ? start.toISOString() : null,
    includesPlacesImages: includesPlacesImages(),
    includesAIPlan: includesAIPlan(),
    includesLevel1: includesLevel1(),
    includesLevel2: includesLevel2(),
  };
}

/**
 * Extract subscription tier from Cognito ID-token claims when present.
 * Supports `custom:subscription_tier`, `subscription_tier`, or cognito group names.
 */
export function tierFromUserClaims(claims: Record<string, unknown> | null | undefined): SubscriptionTier | null {
  if (!claims) return null;

  const direct =
    parseSubscriptionTier(
      typeof claims['custom:subscription_tier'] === 'string'
        ? claims['custom:subscription_tier']
        : typeof claims.subscription_tier === 'string'
          ? claims.subscription_tier
          : null
    ) ?? null;
  if (direct) return direct;

  const groups = claims['cognito:groups'];
  if (Array.isArray(groups)) {
    const lower = groups.map((g) => String(g).toLowerCase());
    if (lower.some((g) => g.includes('premium'))) return 'premium';
    if (lower.some((g) => g.includes('basic'))) return 'basic';
  }

  return null;
}

/**
 * Called on login / session restore (same role as iOS SubscriptionService.init + future backend sync).
 * Ensures trial is started, applies claim/cached tier, and returns a snapshot for UI.
 */
export async function refreshSubscriptionOnLogin(
  userClaims?: Record<string, unknown> | null
): Promise<SubscriptionSnapshot> {
  getOrSetTrialStartDate();

  const fromClaims = tierFromUserClaims(userClaims ?? null);
  if (fromClaims) {
    updateTier(fromClaims);
  } else {
    // Keep cached tier (defaults to free) until a backend entitlement API exists.
    actualTier = loadCachedTier();
  }

  const snapshot = getSubscriptionSnapshot();
  logTrialStatus(snapshot);
  return snapshot;
}

function logTrialStatus(snapshot: SubscriptionSnapshot): void {
  if (!snapshot.enforceSubscriptions) {
    console.info('SubscriptionService: Enforcement disabled — all features enabled');
    return;
  }
  if (snapshot.isTrialActive) {
    console.info(
      `SubscriptionService: Trial active — ${snapshot.trialDaysRemaining} days remaining (all features enabled)`
    );
    return;
  }
  console.info(`SubscriptionService: Trial expired — using ${snapshot.actualTierDisplayName} tier`);
}
